import React, { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Alert, Button, Dialog, Field, Input, SegmentedControl, cx } from '@/components/ui';
import { apiService } from '@/services/apiService';
import { notificationService } from '@/services/notificationService';
import { playClickSound } from '@/utils/soundUtils';
import { CountrySelectWithSearch } from '@/features/auth/components/CountrySelectWithSearch';
import '@/features/auth/components/PhoneInputAuth.css';

type Channel = 'email' | 'phone';

function onlyDigits6(value: string) {
  return value.replace(/\D/g, '').slice(0, 6);
}

/** apiService rejects with a plain `{ message }` object, not an Error. */
function errorMessage(err: unknown, fallback: string): string {
  const message = (err as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message.trim() ? message.trim() : fallback;
}

export interface VerifyAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialEmail?: string | null;
  initialPhone?: string | null;
  onVerified?: () => void;
  /** Limit which OTP channels are shown. Default: both. */
  allowedChannels?: Channel[];
  /** Channels the account has already verified — never offered again. */
  verifiedChannels?: Channel[];
  /**
   * True when every allowed channel must be verified (not just one). The modal then stays open
   * and moves on to the next unverified channel instead of closing after the first success.
   */
  requireAll?: boolean;
}

export default function VerifyAccountModal({
  isOpen,
  onClose,
  initialEmail,
  initialPhone,
  onVerified,
  allowedChannels,
  verifiedChannels,
  requireAll = false,
}: VerifyAccountModalProps) {
  const { t } = useTranslation();
  const phoneInputId = useId();
  /** Channels verified in this modal session (on top of `verifiedChannels`). */
  const [justVerified, setJustVerified] = useState<Channel[]>([]);
  const allowedKey = (allowedChannels ?? []).join(',');
  const verifiedKey = [...(verifiedChannels ?? []), ...justVerified].join(',');
  const channels: Channel[] = useMemo(() => {
    const allowed: Channel[] = allowedKey ? (allowedKey.split(',') as Channel[]) : ['email', 'phone'];
    const unique = allowed.filter((c, i, arr) => arr.indexOf(c) === i);
    const done = verifiedKey.split(',');
    const remaining = unique.filter((c) => !done.includes(c));
    // Nothing left to verify should not happen (the caller would not open the modal); fall back
    // to the full list rather than rendering an empty modal.
    return remaining.length ? remaining : unique;
  }, [allowedKey, verifiedKey]);
  const hasEmail = useMemo(() => String(initialEmail ?? '').trim().length > 0, [initialEmail]);
  const hasPhone = useMemo(() => String(initialPhone ?? '').trim().length > 0, [initialPhone]);

  const defaultChannel: Channel = channels.includes('email') && (hasEmail || !channels.includes('phone'))
    ? 'email'
    : (channels.includes('phone') ? 'phone' : channels[0]);

  const [channel, setChannel] = useState<Channel>(defaultChannel);
  const [email, setEmail] = useState<string>(String(initialEmail ?? '').trim());
  const [phone, setPhone] = useState<string>(String(initialPhone ?? '').trim());
  const [otp, setOtp] = useState('');

  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (isOpen) setJustVerified([]);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    // Initialize state from props on open to keep it fresh.
    setChannel(defaultChannel);
    setEmail(String(initialEmail ?? '').trim());
    setPhone(String(initialPhone ?? '').trim());
    setOtp('');
    setSent(false);
    setSending(false);
    setVerifying(false);
  }, [isOpen, defaultChannel, initialEmail, initialPhone]);

  const canSend = useMemo(() => {
    if (sending || verifying) return false;
    if (channel === 'email') return email.trim().includes('@');
    return phone.trim().length >= 8; // server validates; this is just basic UX gating
  }, [channel, email, phone, sending, verifying]);

  const canVerify = useMemo(() => {
    if (sending || verifying) return false;
    return onlyDigits6(otp).length === 6;
  }, [otp, sending, verifying]);

  const title = channel === 'email' ? 'Verify with email' : 'Verify with phone';

  const sendOtp = async () => {
    if (!canSend) return;
    playClickSound();
    setSending(true);
    try {
      if (channel === 'email') {
        const res = await apiService.sendEmailChangeOTP(email.trim());
        if (!res.success) throw new Error(res.message || 'Failed to send OTP');
        notificationService.success(res.message || 'OTP sent to your email');
      } else {
        const res = await apiService.sendPhoneChangeOTP(phone.trim());
        if (!res.success) throw new Error(res.message || 'Failed to send OTP');
        notificationService.success(res.message || 'OTP sent to your phone');
      }
      setSent(true);
    } catch (err: unknown) {
      notificationService.error(errorMessage(err, 'Failed to send OTP'));
    } finally {
      setSending(false);
    }
  };

  const verifyOtp = async () => {
    if (!canVerify) return;
    playClickSound();
    setVerifying(true);
    try {
      const cleaned = onlyDigits6(otp);
      if (channel === 'email') {
        const res = await apiService.verifyEmailChange(email.trim(), cleaned);
        if (!res.success) throw new Error(res.message || 'Verification failed');
      } else {
        const res = await apiService.verifyPhoneChange(phone.trim(), cleaned);
        if (!res.success) throw new Error(res.message || 'Verification failed');
      }

      // Every allowed channel is required and one is still unverified: stay open and move on to it.
      const stillToVerify = channels.filter((c) => c !== channel);
      if (requireAll && stillToVerify.length > 0) {
        notificationService.success(
          channel === 'phone'
            ? 'Phone verified. Now verify your email.'
            : 'Email verified. Now verify your phone number.'
        );
        setJustVerified((prev) => [...prev, channel]);
        setChannel(stillToVerify[0]);
        setOtp('');
        setSent(false);
        return;
      }

      notificationService.success('Verified successfully');
      onClose();
      onVerified?.();
    } catch (err: unknown) {
      notificationService.error(errorMessage(err, 'Verification failed'));
    } finally {
      setVerifying(false);
    }
  };

  const busy = sending || verifying;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title="Verify account"
      size="sm"
      // Centred on every screen, like the Welcome Bonus sheet it follows. As a bottom sheet on a
      // narrow (kiosk / phone) screen it sat under the floating widget, which covered the OTP field.
      placement="center"
      dismissible={!busy}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              playClickSound();
              onClose();
            }}
            disabled={busy}
          >
            Close
          </Button>
          <Button
            variant="primary"
            loading={verifying}
            onClick={() => void verifyOtp()}
            disabled={!canVerify || !sent}
          >
            {verifying ? 'Verifying…' : 'Verify'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Alert tone="info">
          {requireAll && channels.length > 1
            ? 'Verify both your phone number and your email: request an OTP for each, then enter the 6-digit code.'
            : channels.length > 1
              ? 'Choose a channel, request an OTP, then enter the 6-digit code to verify your account.'
              : 'Request an OTP, then enter the 6-digit code to verify your account.'}
        </Alert>

        {channels.length > 1 && (
          <SegmentedControl<Channel>
            aria-label={t('accountSettings.verifyModal.channelLabel', 'Verification method')}
            items={[
              { value: 'email', label: 'Email' },
              { value: 'phone', label: 'Phone' },
            ]}
            value={channel}
            onChange={(next) => {
              if (busy) return;
              setChannel(next);
              setSent(false);
              setOtp('');
            }}
          />
        )}

        {channel === 'email' ? (
          <Field label="Email" hint={hasEmail ? 'We will send the OTP to your existing email.' : undefined}>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="you@example.com"
              disabled={busy || hasEmail}
            />
          </Field>
        ) : (
          <div className="flex min-w-0 flex-col gap-1.5">
            <label htmlFor={phoneInputId} className="font-ui text-small font-medium text-secondary">
              Phone
            </label>
            <div
              className={cx(
                'flex h-11 min-w-0 items-center rounded-control border border-edge px-3 font-ui text-body text-primary',
                busy || hasPhone
                  ? 'border-dashed bg-transparent'
                  : 'bg-inset hover:border-edge-strong focus-within:border-focus focus-within:ring-3 focus-within:ring-focus/20'
              )}
            >
              <PhoneInput
                international={false}
                defaultCountry="US"
                countryOptionsOrder={['US', 'CA', 'GB', 'VN', '…']}
                addInternationalOption={false}
                placeholder=""
                value={phone || undefined}
                onChange={(v) => setPhone(v ?? '')}
                countrySelectComponent={CountrySelectWithSearch}
                className="PhoneInputAuth w-full min-w-0"
                disabled={busy || hasPhone}
                numberInputProps={{
                  id: phoneInputId,
                  'aria-label': 'Phone number',
                  autoComplete: 'tel',
                }}
              />
            </div>
            {hasPhone && (
              <p className="m-0 font-ui text-small text-tertiary">We will send the OTP to your existing phone number.</p>
            )}
          </div>
        )}

        {/* Code first, then the send button; side by side from sm up (stacked on phones so nothing overflows). */}
        <div className="flex flex-col gap-2 @bp-sm:flex-row">
          <Input
            value={otp}
            onChange={(e) => setOtp(onlyDigits6(e.target.value))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="OTP"
            disabled={busy}
            containerClassName="@bp-sm:flex-1"
          />
          <Button
            variant="secondary"
            loading={sending}
            onClick={() => void sendOtp()}
            disabled={!canSend}
            className="w-full @bp-sm:w-auto"
          >
            {sending ? 'Sending…' : 'Send OTP'}
          </Button>
        </div>

        <p className="m-0 font-ui text-small text-tertiary">
          {sent ? `OTP sent. ${title}.` : 'Send OTP to start verification.'}
        </p>
      </div>
    </Dialog>
  );
}
